/*
 * The retro kit (integration plan slice 6b; the designer: "simple, like a retro SFC game, just for the readability a
 * darkly blurred part at the back"). One kind of window, `.ab-panel` (and `.ab-window`, the same window drawn on a
 * button: the pad's buttons and the menu button): a dark translucent fill with the canvas blurred behind it, a 1 px
 * light edge and a 2 px dark line inside it (an inset ring, not a shadow), square corners, no gradient, no icon, no
 * shadow, no text-shadow. Where the browser cannot blur (`@supports`, or the shell's `.ab-no-blur` when
 * `CSS.supports` says so), the fill alone, darker, keeps the text readable. Every colour and size is a variable
 * here; the shell feeds --ab-text and --ab-panel from the dungeon's theme in both scenes, and the fill is a share of
 * --ab-panel. Placements: `bottom` (a full-width strip above the pad's row: the dialogue), `center` (menus,
 * banners), `corner` (the location's name top left; the dungeon's band, in its grid), `prompt` (the one word, bottom
 * right). Every fixed piece keeps clear of the safe areas; every target is at least 44 px. With
 * `prefers-reduced-motion: reduce` nothing blinks or fades: the ▼ stays lit, the location's name leaves at once.
 */
:root {
  /* The fill over the blurred canvas: 80% keeps the theme's text at 6:1 even over a white backdrop (74% gave 4.9:1). */
  --ab-panel-fill: color-mix(in srgb, var(--ab-panel, #080a10) 80%, transparent);
  --ab-panel-fill-solid: color-mix(in srgb, var(--ab-panel, #080a10) 90%, transparent);
  /* A pressed button of the pad: the fill lit a little by the text colour. */
  --ab-panel-fill-pressed: color-mix(in srgb, var(--ab-panel, #080a10) 70%, var(--ab-text, #f4f1e6) 20%);
  --ab-panel-blur: 6px;
  --ab-panel-edge: color-mix(in srgb, var(--ab-text, #f4f1e6) 80%, transparent);
  --ab-panel-inner: rgba(0, 0, 0, 0.55);
  /* The window's lines: the light edge, the dark line inside it, the corners (2 px at most). */
  --ab-edge-width: 1px;
  --ab-inner-width: 2px;
  --ab-radius: 2px;
  --ab-panel-pad: 10px 12px;
  /* The dungeon's band keeps its height budget (15% of the viewport): a tighter inset. */
  --ab-panel-pad-tight: 3px 8px;
  --ab-panel-size: 14px;
  /* The minimap's corner words (its title and the north mark): the band's smallest text. */
  --ab-minimap-label-size: 9px;
  --ab-panel-leading: 1.4;
  --ab-name: #f2c98a;
  /* A warning line (slice 8h: tickets that waste time): a warm red, well clear of the text on the panel's fill. */
  --ab-warning: #ff9e8a;
  --ab-cursor: var(--ab-text, #f4f1e6);
  --ab-target: 44px;
  /* How far a window stays from the screen's edges (beside the safe areas), and the top row's (the menu button, the band). */
  --ab-margin: 8px;
  --ab-margin-top: 4px;
  /* The widest bottom strip, a centred menu's least width, what a centred menu leaves free around it. */
  --ab-strip-max: 720px;
  --ab-menu-min: 240px;
  --ab-gutter: 32px;
  /* The dialogue's room for the ▼ at its right, and the ▼'s place in it. */
  --ab-more-room: 28px;
  --ab-more-right: 10px;
  --ab-more-bottom: 6px;
  /* A menu row's columns: the cursor's width and the gap between columns; a disabled row's (or button's) dimming. */
  --ab-cursor-width: 1.1em;
  --ab-menu-gap: 10px;
  /* An adjustable row's value between its − and + (slice 8f): room for "×10" so the targets do not move. */
  --ab-step-amount: 2.6em;
  /* Where a row's label starts (a heading and a menu's lines line up with it), how far a row under a heading is set in, the gap under a menu's lines. */
  --ab-label-inset: calc(var(--ab-bar) + var(--ab-cursor-width) + var(--ab-menu-gap));
  --ab-under-inset: 1em;
  --ab-line-gap: 6px;
  --ab-dim: 0.45;
  /* The prompt: its inset and the gap between the key and the word. */
  --ab-prompt-pad: 0 14px;
  --ab-prompt-gap: 8px;
  /* The pad (slice 4) in the kit's window: its margin from the corners (tighter on a landscape phone), the gap between buttons, a glyph's size. */
  --ab-pad-margin: 10px;
  --ab-pad-margin-landscape: 6px;
  --ab-pad-gap: 4px;
  --ab-glyph-size: 20px;
  /* The menu button's three bars: their length, thickness and the gap between them. */
  --ab-bars-width: 18px;
  --ab-bar: 2px;
  --ab-bars-gap: 4px;
  /* The dungeon's band: its type (the status and help; the log and help line smaller), leading, gaps, the log's widest. */
  --ab-band-size: 12px;
  --ab-band-small: 11px;
  --ab-band-leading: 1.3;
  --ab-band-gap: 6px;
  --ab-band-row-gap: 2px;
  --ab-log-max: 560px;
  /* A window's title tab (Q52): how far in from the window's left edge it sits, and the room a titled window keeps at its top for the tab's lower half. */
  --ab-tab-inset: 10px;
  --ab-tab-room: calc(var(--ab-panel-size) * var(--ab-panel-leading) / 2 + 12px);
  /* The kit's text fields (the login): a field's inner padding; its height is a 44 px target. */
  --ab-input-pad: 0 10px;
  /* Layers: the portal's veil (under everything), the location's name and the band, the catcher under a window, the pad and the menu button, a menu or a dialogue, a banner (above every window). */
  --ab-z-fade: 1;
  --ab-z-corner: 2;
  --ab-z-veil: 3;
  --ab-z-controls: 4;
  --ab-z-window: 5;
  --ab-z-banner: 6;
  /* How far the bottom strip stays above the screen's edge: the pad's row when the pad shows (the shell sets it). */
  --ab-bottom-reserve: 0px;
}
@media (min-width: 900px) {
  :root {
    --ab-panel-size: 16px;
  }
}
/* With the touch pad (the shell's `ab-touch`): the bottom strip sits above the pad's row (three buttons, two gaps, its margin). */
:root.ab-touch {
  --ab-bottom-reserve: calc(3 * var(--ab-target) + 2 * var(--ab-pad-gap) + var(--ab-pad-margin));
  /* The pad's width from the screen's side (three buttons, two gaps, its margin). */
  --ab-side-reserve: calc(3 * var(--ab-target) + 2 * var(--ab-pad-gap) + var(--ab-pad-margin));
}
/* The one window: a panel, or a button drawn as one. */
.ab-panel,
.ab-window {
  box-sizing: border-box;
  color: var(--ab-text);
  background: var(--ab-panel-fill);
  -webkit-backdrop-filter: blur(var(--ab-panel-blur));
  backdrop-filter: blur(var(--ab-panel-blur));
  border: var(--ab-edge-width) solid var(--ab-panel-edge);
  border-radius: var(--ab-radius);
  box-shadow: inset 0 0 0 var(--ab-inner-width) var(--ab-panel-inner);
  text-shadow: none;
  user-select: none;
  -webkit-user-select: none;
}
.ab-panel {
  margin: 0;
  padding: var(--ab-panel-pad);
  font: var(--ab-panel-size) / var(--ab-panel-leading) var(--ab-font, system-ui, sans-serif);
}
.ab-no-blur .ab-panel,
.ab-no-blur .ab-window {
  -webkit-backdrop-filter: none;
  backdrop-filter: none;
  background: var(--ab-panel-fill-solid);
}
@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .ab-panel,
  .ab-window {
    background: var(--ab-panel-fill-solid);
  }
}
.ab-panel p {
  margin: 0;
}
.ab-panel-bottom {
  position: fixed;
  left: calc(var(--ab-margin) + env(safe-area-inset-left));
  right: calc(var(--ab-margin) + env(safe-area-inset-right));
  bottom: calc(var(--ab-bottom-reserve) + var(--ab-margin) + env(safe-area-inset-bottom));
  max-width: var(--ab-strip-max);
  margin: 0 auto;
  min-height: var(--ab-target);
  z-index: var(--ab-z-window);
}
.ab-panel-center {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  min-width: min(var(--ab-menu-min), calc(100vw - var(--ab-gutter)));
  max-width: calc(100vw - var(--ab-gutter) - env(safe-area-inset-left) - env(safe-area-inset-right));
  max-height: calc(100vh - var(--ab-gutter) - env(safe-area-inset-top) - env(safe-area-inset-bottom));
  overflow-y: auto;
  z-index: var(--ab-z-window);
}
/*
 * With the touch pad (slice 8f fix round): a centred window never covers it. In portrait it stays above the pad's
 * row (the bottom reserve kept free at both ends, so it stays centred); in landscape it stays between the pad's
 * column and the same width on the other side.
 */
@media (orientation: portrait) {
  :root.ab-touch .ab-panel-center {
    max-height: calc(100vh - 2 * (var(--ab-bottom-reserve) + var(--ab-margin)) - env(safe-area-inset-top) - env(safe-area-inset-bottom));
  }
}
@media (orientation: landscape) {
  :root.ab-touch .ab-panel-center {
    max-width: calc(100vw - 2 * (var(--ab-side-reserve) + var(--ab-margin)) - env(safe-area-inset-left) - env(safe-area-inset-right));
  }
}
.ab-panel-corner {
  position: fixed;
  top: calc(var(--ab-margin) + env(safe-area-inset-top));
  left: calc(var(--ab-margin) + env(safe-area-inset-left));
  max-width: calc(100vw - 3 * var(--ab-margin) - var(--ab-target) - env(safe-area-inset-left) - env(safe-area-inset-right));
  z-index: var(--ab-z-corner);
}
.ab-panel-prompt {
  position: fixed;
  right: calc(var(--ab-pad-margin) + env(safe-area-inset-right));
  bottom: calc(var(--ab-pad-margin) + env(safe-area-inset-bottom));
  padding: 0;
  z-index: var(--ab-z-corner);
}
/* Under a menu or a dialogue: takes every tap so nothing reaches the canvas (the pad stays above it). */
.ab-veil {
  position: fixed;
  inset: 0;
  z-index: var(--ab-z-veil);
  touch-action: none;
}
/* The location's name: shown on arrival, then faded; its top and height are the menu button's, so the two sit on one line. */
.ab-location {
  top: calc(var(--ab-margin-top) + env(safe-area-inset-top));
  display: flex;
  align-items: center;
  min-height: var(--ab-target);
  padding-block: 0;
  transition-property: opacity;
  transition-duration: var(--ab-fade-ms, 0s);
  pointer-events: none;
}
.ab-location[data-shown='false'] {
  opacity: 0;
}
/* The dialogue: the speaker's name on its own line, the page, the blinking mark when more follow. */
.ab-dialogue {
  padding-right: var(--ab-more-room);
}
/* The dialogue and a menu's list hold the focus while open; the ▶ shows where a menu is, so no ring. */
.ab-dialogue:focus,
.ab-menu ul:focus {
  outline: none;
}
.ab-dialogue-name {
  color: var(--ab-name);
}
/* Where the browser supports it, `pretty` keeps a lone word off a page's last line; elsewhere the page wraps as usual. Checked at 390 px with the property off (the UI taste pass's fix round): every English page breaks the same, none ends on a lone word. */
.ab-dialogue-text {
  text-wrap: pretty;
}
.ab-dialogue-more {
  position: absolute;
  right: var(--ab-more-right);
  bottom: var(--ab-more-bottom);
  color: var(--ab-cursor);
  animation: ab-blink var(--ab-blink-ms) steps(1, end) infinite;
}
@keyframes ab-blink {
  50% {
    opacity: 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .ab-dialogue-more {
    animation: none;
  }
  .ab-location {
    transition-duration: 0s;
  }
}
/* The one list: a cursor column, the label, a right-aligned value; each row a 44 px target. */
.ab-menu ul {
  list-style: none;
  margin: 0;
  padding: 0;
}
/*
 * A window's title (Q52, ADR 0034): a small panel over the window's top-left corner, its middle on the window's edge
 * (Bravely Default's way), only when the window has a title. The rows scroll inside the window, not the window
 * itself, so the tab is never clipped; a titled window keeps room at its top for the tab's lower half.
 */
.ab-panel-center.ab-menu {
  display: flex;
  flex-direction: column;
  overflow: visible;
  /*
   * As wide as its longest row, up to the max-width (the slice 8f taste pass): centred from `left: 50%`, a window
   * would otherwise shrink to the half screen right of the middle and wrap "Health Potion" beside its − ×5 + at 390 px.
   */
  width: max-content;
}
.ab-menu[data-titled='true'] {
  padding-top: var(--ab-tab-room);
}
.ab-menu ul {
  min-height: 0;
  overflow-y: auto;
}
.ab-panel-tab {
  position: absolute;
  top: 0;
  left: var(--ab-tab-inset);
  max-width: calc(100% - 2 * var(--ab-tab-inset));
  padding: var(--ab-panel-pad-tight);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  transform: translateY(-50%);
  color: var(--ab-name);
}
/* The kit's text field (the login, ADR 0034): the panel's edge and fill, a 44 px target, the page's face. */
.ab-input {
  box-sizing: border-box;
  width: 100%;
  min-height: var(--ab-target);
  margin: 0;
  padding: var(--ab-input-pad);
  color: var(--ab-text);
  background: var(--ab-panel-fill-solid);
  border: var(--ab-edge-width) solid var(--ab-panel-edge);
  border-radius: var(--ab-radius);
  box-shadow: inset 0 0 0 var(--ab-inner-width) var(--ab-panel-inner);
  font: inherit;
  user-select: text;
  -webkit-user-select: text;
}
.ab-input:focus {
  outline: var(--ab-edge-width) solid var(--ab-cursor);
  outline-offset: 1px;
}
/*
 * The login's fields scroll inside the window when they outgrow it (slice 8h: New account's four fields on a landscape
 * phone, 390 px tall), so its two rows always stay in view under them.
 */
.ab-login form {
  min-height: 0;
  margin: 0;
  overflow-y: auto;
}
.ab-login ul {
  flex: none;
}
.ab-login-field {
  display: grid;
  gap: var(--ab-band-row-gap);
  margin: 0 0 var(--ab-menu-gap);
}
.ab-login-label {
  color: var(--ab-name);
}
/* The time zone's list (slice 8h): the kit's field; its open list in the panel's colours, not the system's. */
.ab-select {
  cursor: pointer;
}
.ab-select option,
.ab-select optgroup {
  color: var(--ab-text);
  background: var(--ab-panel, #080a10);
}
/* The one line under a field (the time zone's, slice 8h): the band's small size. */
.ab-login-note {
  font-size: var(--ab-band-small);
  line-height: var(--ab-band-leading);
}
.ab-menu-row {
  display: grid;
  grid-template-columns: var(--ab-cursor-width) minmax(0, 1fr) auto;
  gap: 0 var(--ab-menu-gap);
  align-items: center;
  width: 100%;
  min-height: var(--ab-target);
  margin: 0;
  padding: 0 var(--ab-bar);
  border: 0;
  color: inherit;
  background: none;
  font: inherit;
  text-align: left;
  touch-action: manipulation;
  cursor: pointer;
}
.ab-menu-row[aria-disabled='true'] {
  opacity: var(--ab-dim);
  cursor: default;
}
.ab-menu-cursor {
  color: var(--ab-cursor);
}
.ab-menu-value {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
/*
 * An adjustable row (slice 8f; its fix round): the row's own button, then − and + either side of its value, each a
 * 44 px button beside the row's (never inside it); the value between them is inert.
 */
.ab-menu-adjustable {
  display: flex;
  align-items: center;
}
.ab-menu-adjustable > .ab-menu-row {
  flex: 1 1 auto;
  min-width: 0;
}
.ab-menu-adjust {
  display: flex;
  flex: none;
  align-items: center;
}
.ab-menu-step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: var(--ab-target);
  min-height: var(--ab-target);
  margin: 0;
  padding: 0;
  border: 0;
  color: var(--ab-cursor);
  background: none;
  font: inherit;
  touch-action: manipulation;
  cursor: pointer;
}
.ab-menu-amount {
  min-width: var(--ab-step-amount);
  text-align: center;
}
/* A heading over rows (slice 8f fix round): the names' colour, not dimmed, in line with the labels; the rows under it set in. */
.ab-menu-heading {
  display: flex;
  align-items: center;
  min-height: var(--ab-target);
  padding-left: var(--ab-label-inset);
  color: var(--ab-name);
}
.ab-menu-row[data-under='true'] .ab-menu-label {
  padding-left: var(--ab-under-inset);
}
/* A row whose owner is waiting (the packing panel's Go while storage is read): dimmed, still a target. */
.ab-menu-row[data-waiting='true'] {
  opacity: var(--ab-dim);
}
/* Lines a menu says above its rows (the bag's slots, "The bag is full."): outside the rows' scroll, always in view. */
.ab-menu-lines {
  flex: none;
  padding: 0 var(--ab-bar) var(--ab-line-gap) var(--ab-label-inset);
}
.ab-menu-line {
  margin: 0;
}
/* Lines below the rows (slice 8h: the skip's confirmation, its time left and the waste of the cursor's row). */
.ab-menu-lines[data-place='below'] {
  padding: var(--ab-line-gap) var(--ab-bar) 0 var(--ab-label-inset);
}
/* A line that warns (the waste of a choice of tickets): the warning colour, nothing else. */
.ab-menu-line[data-tone='warning'] {
  color: var(--ab-warning);
}
/* The one word, and its key on a keyboard device. */
.ab-prompt-button {
  display: flex;
  align-items: center;
  gap: var(--ab-prompt-gap);
  min-width: var(--ab-target);
  min-height: var(--ab-target);
  margin: 0;
  padding: var(--ab-prompt-pad);
  border: 0;
  color: inherit;
  background: none;
  font: inherit;
  touch-action: manipulation;
  cursor: pointer;
}
.ab-prompt-key {
  padding: 0 calc(var(--ab-margin) / 2);
  border: var(--ab-edge-width) solid var(--ab-panel-edge);
  border-radius: var(--ab-radius);
  font: inherit;
  font-size: 0.85em;
}
/* The menu button: the corner's 44 px target, drawn as the kit's window; its glyph is three CSS bars (no font needed). */
.ab-menu-button {
  position: fixed;
  top: calc(var(--ab-margin-top) + env(safe-area-inset-top));
  right: calc(var(--ab-margin) + env(safe-area-inset-right));
  width: var(--ab-target);
  height: var(--ab-target);
  margin: 0;
  padding: 0;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  cursor: pointer;
  /* Above a window's catcher: a second press (or a tap in the corner) closes the pause menu. */
  z-index: var(--ab-z-controls);
}
.ab-menu-button::before,
.ab-menu-button::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  box-sizing: border-box;
  width: var(--ab-bars-width);
  transform: translate(-50%, -50%);
}
/* The top and bottom bars (the box's edges), and the middle one. */
.ab-menu-button::before {
  height: calc(3 * var(--ab-bar) + 2 * var(--ab-bars-gap));
  border-top: var(--ab-bar) solid currentColor;
  border-bottom: var(--ab-bar) solid currentColor;
}
.ab-menu-button::after {
  height: var(--ab-bar);
  background: currentColor;
}
/* While a dialogue, a shop or a crossing is up, the menu waits (one window at a time). */
.ab-menu-button[aria-disabled='true'] {
  opacity: var(--ab-dim);
  cursor: default;
}

/*
 * The dungeon's band (integration plan slice 4, the retro way since slice 6b): the status line, the collapsed log
 * and the help line in one corner panel (a grid whose areas change with the orientation), the minimap beside it in
 * its own panel, the menu button in the corner to their right. The band keeps its small type and a tighter inset:
 * it stays within 15% of the viewport height (the ui AGENTS rule, checked by apps/web/test/touch.spec.ts at 390×844
 * and 844×390). Nothing in it is clipped to fit that budget: the status and the help line wrap if they must (the
 * test checks no text overflows its box or the viewport); only a collapsed log line may end in an ellipsis, and a
 * tap expands the log. The chrome never selects as text; only the log takes pointer input.
 */
.ab-hud {
  position: fixed;
  top: calc(var(--ab-margin-top) + env(safe-area-inset-top));
  left: calc(var(--ab-margin) + env(safe-area-inset-left));
  right: calc(var(--ab-margin) + var(--ab-target) + var(--ab-band-gap) + env(safe-area-inset-right));
  display: flex;
  gap: var(--ab-band-gap);
  align-items: flex-start;
  color: var(--ab-text);
  font: var(--ab-band-size) / var(--ab-band-leading) var(--ab-font, system-ui, sans-serif);
  pointer-events: none;
  user-select: none;
  -webkit-user-select: none;
  z-index: var(--ab-z-corner);
}
.ab-hud .ab-band {
  position: static;
  flex: 1 1 auto;
  min-width: 0;
  max-width: none;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas:
    'status'
    'log'
    'help';
  gap: var(--ab-band-row-gap) var(--ab-margin);
  padding: var(--ab-panel-pad-tight);
  font: var(--ab-band-size) / var(--ab-band-leading) var(--ab-font, system-ui, sans-serif);
}
.ab-status {
  grid-area: status;
  display: flex;
  flex-wrap: wrap;
  gap: 0 var(--ab-menu-gap);
  min-width: 0;
  font-weight: 600;
}
.ab-status > * {
  white-space: nowrap;
}
.ab-log {
  --ab-log-lines: var(--ab-log-lines-portrait);
  grid-area: log;
  position: relative;
  min-width: 0;
  max-width: var(--ab-log-max);
}
.ab-log ol {
  list-style: none;
  margin: 0;
  padding: 0 calc(var(--ab-target) / 2) 0 0;
  font-size: var(--ab-band-small);
  line-height: var(--ab-band-leading);
  max-height: calc(var(--ab-log-lines) * var(--ab-band-leading) * 1em);
  overflow: hidden;
  pointer-events: auto;
}
.ab-log li {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ab-log-expanded ol {
  max-height: 40vh;
  overflow-y: auto;
}
.ab-log-expanded li {
  white-space: normal;
}
/* A 44 px hit target (the pad's rule) around a small glyph: the padding, not the glyph, makes it big. */
.ab-log-toggle {
  position: absolute;
  top: 0;
  right: 0;
  box-sizing: border-box;
  width: var(--ab-target);
  height: var(--ab-target);
  padding: 0 0 calc(var(--ab-target) / 2) calc(var(--ab-target) / 2);
  border: 0;
  font-size: var(--ab-band-size);
  line-height: calc(var(--ab-target) / 2);
  color: var(--ab-text);
  background: none;
  pointer-events: auto;
  cursor: pointer;
}
.ab-log-toggle::before {
  content: '\25BE';
}
.ab-log-expanded .ab-log-toggle::before {
  content: '\25B4';
}
.ab-help {
  grid-area: help;
  min-width: 0;
  margin: 0;
  /* Where the help line wraps (the touch line in portrait), it breaks between its pairs, never inside a CJK word. */
  word-break: keep-all;
  font-size: var(--ab-band-small);
  opacity: 0.85;
}
@media (orientation: landscape) {
  /* The left column fits its text up to half the band: a phone's short touch help, a desktop's full key help. */
  .ab-hud .ab-band {
    grid-template-columns: fit-content(50%) minmax(0, 1fr);
    grid-template-areas:
      'status log'
      'help log';
  }
  .ab-log {
    --ab-log-lines: var(--ab-log-lines-landscape);
    justify-self: center;
    width: 100%;
  }
}
.ab-panel.ab-stats {
  top: calc(16vh + env(safe-area-inset-top));
  left: calc(6px + env(safe-area-inset-left));
  max-width: min(320px, calc(100vw - 12px - env(safe-area-inset-left) - env(safe-area-inset-right)));
  padding: 6px 8px;
  font: 11px/1.3 var(--ab-font, system-ui, sans-serif);
  user-select: text;
  -webkit-user-select: text;
}
.ab-stats dl {
  margin: 0 0 4px;
}
.ab-stats dl > div {
  display: flex;
  gap: 6px;
}
.ab-stats dt {
  opacity: 0.8;
}
.ab-stats textarea {
  width: 100%;
  font: 10px/1.2 monospace;
}
.ab-stats p {
  margin: 4px 0 2px;
}
.ab-stats dd {
  margin: 0;
  overflow-wrap: anywhere;
}
/* The short fade of a portal crossing (slice 6a): the shell's veil over the canvas, under the HUD. */
.ab-fade {
  position: fixed;
  inset: 0;
  background: var(--ab-panel);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
  z-index: var(--ab-z-fade);
}
.ab-fade[data-on='true'] {
  opacity: 1;
}
